import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import UseAxios from '../../../../../Api/http/http';
import { useAuth } from '../../../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../../../Constants/modules';
import { getProjectDetailsMilestone } from '../../../../../Api/request/projectDetails';
import exportToExcel from '../../../../../Utils/exportToExcel';
import ExpandableSection from '../../expandableSection';
import ProjectMilestoneTable from './projectMilestoneTable';

const EMPTY_FLAGS = {
  ms_v: false,
  po_number_view: false,
  po_link_view: false,
  ms_amt: false,
  replanned_due_date_view: false,
  ms_inv: false,
  ms_rdt: false,
  ms_rmk: false,
  collection_amount_view: false,
  collection_date_view: false,
  ms_e: false,
  ms_a: false,
};

const getResponseMessage = (response, fallbackMessage) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.message
  || fallbackMessage
);

const isBlankValue = (value) => value === null || value === undefined || value === '';

const EXCEL_DATE_EPOCH_UTC = Date.UTC(1899, 11, 30);

const parseDateParts = (value) => {
  if (isBlankValue(value)) return '';
  const isoLikeValue = String(value).split('T')[0].split(' ')[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoLikeValue);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
    };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

const parseExportDateValue = (value) => {
  const dateParts = parseDateParts(value);
  if (!dateParts || typeof dateParts === 'string') return dateParts;

  const excelDateSerial = (
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day) - EXCEL_DATE_EPOCH_UTC
  ) / (24 * 60 * 60 * 1000);

  return excelDateSerial;
};

const formatExportMoneyValue = (value) => {
  if (isBlankValue(value)) return '';
  return String(value);
};

const formatExportNumericValue = (value) => {
  if (isBlankValue(value)) return '';
  return String(value);
};

const formatPercentValue = (value) => {
  if (isBlankValue(value)) return '';
  const percent = Number(value);
  if (Number.isNaN(percent)) return String(value);
  return `${percent}%`;
};

const hasPermissionAction = (permissions, moduleName, action) => (
  Array.isArray(permissions?.[moduleName]) && permissions[moduleName].includes(action)
);

const isSuperAdminUser = (userProfile = {}) => {
  if (userProfile?.superAdmin || userProfile?.super_admin) return true;

  const roleDetails = Array.isArray(userProfile?.roleDetails)
    ? userProfile.roleDetails
    : userProfile?.roleDetails
      ? [userProfile.roleDetails]
      : [];

  return roleDetails.some((role) => {
    const roleName = String(role?.roleName || role?.name || '').trim()
      .toLowerCase();
    const roleId = Number(role?.role_id ?? role?.roleId);
    return roleName === 'super admin' || roleId === 1;
  });
};

const getMilestoneExportColumns = (flags = EMPTY_FLAGS) => ([
  { key: 'po_number', label: 'PO Number', show: Boolean(flags.po_number_view) },
  { key: 'po_link', label: 'PO Link', show: Boolean(flags.po_link_view) },
  { key: 'milestone_id', label: 'Milestone Id', show: true },
  { key: 'milestone_status', label: 'Milestone Status', show: true },
  { key: 'invoice_status', label: 'Invoice Status', show: true },
  { key: 'milestone', label: 'Milestone', show: true },
  { key: 'category', label: 'Category', show: true },
  {
    key: 'cont_value_p',
    label: '% (Of Contract Value)',
    show: true,
    format: formatPercentValue,
  },
  {
    key: 'amount',
    label: 'Amount (INR)',
    show: Boolean(flags.ms_amt),
    format: formatExportMoneyValue,
  },
  {
    key: 'cogs',
    label: 'COGS (INR)',
    show: Boolean(flags.ms_amt),
    format: formatExportMoneyValue,
  },
  {
    key: 'ga',
    label: 'GA (INR)',
    show: Boolean(flags.ms_amt),
    format: formatExportMoneyValue,
  },
  {
    key: 'tp',
    label: 'TP (INR)',
    show: Boolean(flags.ms_amt),
    format: formatExportMoneyValue,
  },
  {
    key: 'due_date',
    label: 'Due Date',
    show: true,
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
  },
  {
    key: 'replanned_due_date',
    label: 'Replanned Due Date',
    show: Boolean(flags.replanned_due_date_view),
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
  },
  { key: 'invoice_id', label: 'Invoice Id', show: true },
  { key: 'zoho_invoice_number', label: 'Zoho Invoice Number', show: true },
  { key: 'invoice_id_related_to_credit_note', label: 'Invoice Id Related to Credit Note', show: true },
  { key: 'invoice_type', label: 'Invoice Type', show: true },
  { key: 'payment_status', label: 'Payment Status', show: true },
  {
    key: 'invoice_amount',
    label: 'Invoice Amount (INR)',
    show: Boolean(flags.ms_inv),
    format: formatExportMoneyValue,
  },
  {
    key: 'invoice_cogs',
    label: 'Invoice COGS (INR)',
    show: Boolean(flags.ms_inv),
    format: formatExportMoneyValue,
  },
  {
    key: 'invoice_ga',
    label: 'Invoice GA (INR)',
    show: Boolean(flags.ms_inv),
    format: formatExportMoneyValue,
  },
  {
    key: 'invoice_tp',
    label: 'Invoice TP (INR)',
    show: Boolean(flags.ms_inv),
    format: formatExportMoneyValue,
  },
  {
    key: 'invoice_amount_with_tax',
    label: 'Invoice Amount with Tax (INR)',
    show: Boolean(flags.ms_inv),
    format: formatExportMoneyValue,
  },
  {
    key: 'invoice_date',
    label: 'Invoice Date',
    show: true,
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
  },
  {
    key: 'revenue_recognition_date',
    label: 'Revenue Recognition Date',
    show: true,
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
  },
  {
    key: 'received_date',
    label: 'Received Date',
    show: Boolean(flags.ms_rdt),
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
  },
  { key: 'billed_effort_hrs', label: 'Billed Effort(hrs)', show: true },
  { key: 'collection_credit_period', label: 'Collection Credit Period', show: true },
  { key: 'date_variance', label: 'Variance', show: true },
  { key: 'remarks', label: 'Remarks', show: Boolean(flags.ms_rmk) },
  { key: 'collection_id', label: 'Collection Id', show: Boolean(flags.collection_amount_view), hidden: true },
  {
    key: 'amount_to_collect',
    label: 'Amount to Collect (INR)',
    show: Boolean(flags.collection_amount_view),
    format: formatExportMoneyValue,
    hidden: true,
  },
  {
    key: 'collection_due_date',
    label: 'Collection Due Date',
    show: Boolean(flags.collection_date_view),
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
    hidden: true,
  },
  {
    key: 'collection_projection_date',
    label: 'Collection Projection Date',
    show: Boolean(flags.collection_date_view),
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
    hidden: true,
  },
  {
    key: 'collected_date',
    label: 'Collected Date',
    show: Boolean(flags.collection_date_view),
    format: parseExportDateValue,
    excelFormat: 'd-mmm-yyyy',
    hidden: true,
  },
  {
    key: 'collected_amount',
    label: 'Collected Amount (INR)',
    show: Boolean(flags.collection_amount_view),
    format: formatExportMoneyValue,
    hidden: true,
  },
  {
    key: 'collection_comment',
    label: 'Collection Comment',
    show: Boolean(flags.collection_amount_view),
    hidden: true,
  },
  {
    key: 'collection_remarks',
    label: 'Collection Remarks',
    show: Boolean(flags.collection_amount_view),
    hidden: true,
  },
]).filter((column) => column.show);

const buildMilestoneTotalsExportRow = (columns = [], totals = {}) => {
  const totalLabelKey = 'milestone_id';

  return columns.reduce((acc, column) => {
    if (column.key === totalLabelKey) {
      acc[column.label] = 'Total';
      return acc;
    }

    if (column.key === 'cont_value_p') {
      acc[column.label] = formatPercentValue(totals?.contractValuePercent);
      return acc;
    }

    if (column.key === 'amount') {
      acc[column.label] = formatExportNumericValue(totals?.amount);
      return acc;
    }

    if (column.key === 'cogs') {
      acc[column.label] = formatExportNumericValue(totals?.cogs);
      return acc;
    }

    if (column.key === 'ga') {
      acc[column.label] = formatExportNumericValue(totals?.ga);
      return acc;
    }

    if (column.key === 'tp') {
      acc[column.label] = formatExportNumericValue(totals?.tp);
      return acc;
    }

    if (column.key === 'invoice_amount') {
      acc[column.label] = formatExportNumericValue(totals?.invoiceAmount);
      return acc;
    }

    if (column.key === 'invoice_cogs') {
      acc[column.label] = formatExportNumericValue(totals?.invoiceCogs);
      return acc;
    }

    if (column.key === 'invoice_ga') {
      acc[column.label] = formatExportNumericValue(totals?.invoiceGa);
      return acc;
    }

    if (column.key === 'invoice_tp') {
      acc[column.label] = formatExportNumericValue(totals?.invoiceTp);
      return acc;
    }

    if (column.key === 'invoice_amount_with_tax') {
      acc[column.label] = formatExportNumericValue(totals?.invoiceAmountWithTax);
      return acc;
    }

    if (column.key === 'amount_to_collect') {
      acc[column.label] = formatExportNumericValue(totals?.amountToCollect);
      return acc;
    }

    if (column.key === 'collected_amount') {
      acc[column.label] = formatExportNumericValue(totals?.collectedAmount);
      return acc;
    }

    acc[column.label] = '';
    return acc;
  }, {});
};

const buildMilestoneExportRows = (rows = [], flags = EMPTY_FLAGS, totals = null) => {
  const columns = getMilestoneExportColumns(flags);
  const exportRows = rows.map((row) => columns.reduce((acc, column) => {
    const rawValue = row?.[column.key];
    const formattedValue = typeof column.format === 'function'
      ? column.format(rawValue)
      : (rawValue ?? '');

    acc[column.label] = formattedValue;
    if (
      column.excelFormat
      && formattedValue !== ''
      && formattedValue !== null
      && formattedValue !== undefined
      && typeof formattedValue !== 'string'
    ) {
      acc.format = {
        ...(acc.format || {}),
        [column.label]: column.excelFormat,
      };
    }
    return acc;
  }, {}));

  if (totals) {
    exportRows.push(buildMilestoneTotalsExportRow(columns, totals));
  }

  return exportRows;
};

const MilestoneSection = ({
  expanded,
  onToggle,
  onDownload,
  onMore,
  onMoreMenuItems,
  showMoreAction,
  projectId,
  handleMilestoneEdit,
  handleInvoiceAdd,
  handleInvoiceEdit,
  handleCollectionAdd,
  handleCollectionEdit,
}) => {
  const { userProfile } = useAuth();

  const [rows, setRows] = useState([]);
  const [flags, setFlags] = useState(EMPTY_FLAGS);
  const [totals, setTotals] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = userProfile?.permissions || {};
  const isSuperAdmin = useMemo(() => isSuperAdminUser(userProfile), [userProfile]);

  const requestQuery = useMemo(() => ({
    projectId,
  }), [projectId]);

  const effectiveFlags = useMemo(() => ({
    ...flags,
    ms_v: isSuperAdmin || hasPermissionAction(permissions, MODULE_NAMES.FINANCE_MILESTONE, 'read'),
    po_number_view: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_PO_NUMBER_VIEW,
      'read',
    ),
    po_link_view: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_PO_LINK_VIEW,
      'read',
    ),
    ms_a: isSuperAdmin || hasPermissionAction(permissions, MODULE_NAMES.FINANCE_MILESTONE, 'create'),
    ms_e: isSuperAdmin || hasPermissionAction(permissions, MODULE_NAMES.FINANCE_MILESTONE, 'update'),
    ms_amt: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_AMOUNT_VIEW,
      'read',
    ),
    replanned_due_date_view: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_REPLANNED_DUE_DATE_VIEW,
      'read',
    ),
    ms_inv: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_INVOICE_AMOUNT_VIEW,
      'read',
    ),
    ms_rdt: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_RECEIVED_DATE_VIEW,
      'read',
    ),
    ms_rmk: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_REMARKS_VIEW,
      'read',
    ),
    collection_amount_view: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_COLLECTION_AMOUNT_VIEW,
      'read',
    ),
    collection_date_view: isSuperAdmin || hasPermissionAction(
      permissions,
      MODULE_NAMES.FINANCE_MILESTONE_COLLECTION_DATE_VIEW,
      'read',
    ),
  }), [flags, isSuperAdmin, permissions]);

  const handleMilestoneDownload = async ({ format, title }) => {
    if (typeof onDownload === 'function' && format !== 'excel') {
      await onDownload({ format, title });
      return;
    }

    if (format !== 'excel') return;

    const exportRows = buildMilestoneExportRows(rows, effectiveFlags, totals);
    if (!exportRows.length) return;

    const columnsConfig = getMilestoneExportColumns(effectiveFlags).map((column) => ({
      header: column.label,
      key: column.label,
      width: 30,
      hidden: Boolean(column.hidden),
    }));

    await exportToExcel({
      data: exportRows,
      workBookTitle: `${String(title || 'Milestone')
        .replaceAll(/[^\w\s-]/g, '')
        .replaceAll(/\s+/g, '_') || 'Milestone'}_report`,
      workSheetTitle: title || 'Milestone',
      columnsConfig,
    });
  };

  const [milestoneLoaded, setMilestoneLoaded] = useState(false);

  useEffect(() => {
    // Only fetch when the section is expanded and not already loaded
    if (!expanded || milestoneLoaded) return;

    let isCancelled = false;

    const fetchMilestoneData = async () => {
      if (!projectId) {
        setRows([]);
        setFlags(EMPTY_FLAGS);
        setTotals(null);
        return;
      }

      setIsLoading(true);
      try {
        const response = await UseAxios(
          getProjectDetailsMilestone(requestQuery, String(Date.now())),
        );

        const responseData = response?.data;
        if (responseData?.name === 'AxiosError' || responseData?.isAxiosError) {
          throw responseData;
        }

        const payload = responseData?.data || {};
        if (!isCancelled) {
          setRows(Array.isArray(payload?.tableData) ? payload.tableData : []);
          setFlags(payload?.flags || EMPTY_FLAGS);
          setTotals(payload?.totals || null);
          setMilestoneLoaded(true);
        }
      } catch (error) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error(getResponseMessage(error, 'Failed to fetch milestone data'));
          setRows([]);
          setFlags(EMPTY_FLAGS);
          setTotals(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchMilestoneData();

    return () => {
      isCancelled = true;
    };
  }, [expanded, milestoneLoaded, projectId, requestQuery]);

  return (
    <ExpandableSection
      title="Milestone"
      expanded={expanded}
      onToggle={onToggle}
      onDownload={handleMilestoneDownload}
      onMore={onMore}
      onMoreMenuItems={onMoreMenuItems}
      showMoreAction={showMoreAction}
    >
      <div className="section-content milestone-section">
        <ProjectMilestoneTable
          rows={rows}
          flags={effectiveFlags}
          totals={totals}
          isLoading={isLoading}
          onMilestoneEdit={handleMilestoneEdit}
          onInvoiceAdd={handleInvoiceAdd}
          onInvoiceEdit={handleInvoiceEdit}
          onCollectionAdd={handleCollectionAdd}
          onCollectionEdit={handleCollectionEdit}
        />
      </div>
    </ExpandableSection>
  );
};

MilestoneSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onMore: PropTypes.func,
  onMoreMenuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
  })),
  showMoreAction: PropTypes.bool,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleMilestoneEdit: PropTypes.func,
  handleInvoiceAdd: PropTypes.func,
  handleInvoiceEdit: PropTypes.func,
  handleCollectionAdd: PropTypes.func,
  handleCollectionEdit: PropTypes.func,
};

MilestoneSection.defaultProps = {
  onDownload: undefined,
  onMore: undefined,
  onMoreMenuItems: [],
  showMoreAction: false,
  projectId: null,
  handleMilestoneEdit: undefined,
  handleInvoiceAdd: undefined,
  handleInvoiceEdit: undefined,
  handleCollectionAdd: undefined,
  handleCollectionEdit: undefined,
};

export default MilestoneSection;
